import { asMarkdownTemplate } from '../../../webmodule-common/other/general/markdown';
import { customElement, state } from 'lit/decorators.js';
import { html, TemplateResult } from 'lit';
import { map } from 'lit/directives/map.js';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base.js';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef
} from '../../../webmodule-common/components/src/components/table/lit-table';
import type { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events.js';
import { LangFranchiseeGuidesView } from '../../../localization/scripts/franchisee/view/franchisee-guides-view-lang';
import {
  exportSchemaVersion,
  purchaseOrderExportFields,
  purchaseOrderExportSampleJson,
  purchaseOrderExportSampleXml,
  quoteExportFields,
  quoteExportSampleJson,
  quoteExportSampleXml,
  type ExportFieldReferenceEntry
} from './export-format-fields.generated.js';

/**
 * One guide in the Guides section.
 *
 * Adding a guide is an entry in {@link FranchiseeGuidesView.guides} plus a method that renders it.
 * Nothing else in this file changes, and nothing outside it does.
 */
interface GuideDefinition {
  /** Stable identifier, used for selection. */
  id: string;
  title: () => string;
  render: () => TemplateResult;
}

/**
 * The Guides section of Settings: reference material for the dealer, read only.
 *
 * Master and detail rather than a single document, because this section holds a growing set of
 * guides and the Export Format guide is only the first one. The navigation is the application's
 * own tree component, so a later guide can sit under a heading without this turning into a
 * different page.
 *
 * The Export Format guide is the only thing standing between a dealer and a data file they cannot
 * interpret: they map our fields onto their own system by hand, in a tool we do not control. Its
 * prose is localised; its field tables and worked samples are generated from the export contract
 * (see export-format-fields.generated.ts) so they cannot drift from the real file. Field names are
 * deliberately not translated, because they are the format.
 */
@customElement('wm-franchiseeguidesview')
export class FranchiseeGuidesView extends PageControlTabWithIndependantSaving {
  @state()
  private selectedGuideId = 'export-format';

  constructor() {
    super();
    this.pageFragment = 'guides';
  }

  /** Reference material, so nothing here is editable by anyone. */
  public isDataReadonly(): boolean {
    return true;
  }

  buttonMenu() {
    return html``;
  }

  public internalDataChanged(): boolean {
    return false;
  }

  public async prepareForSave(): Promise<void> {
    //
  }

  public allowDeletePage(): boolean {
    return false;
  }

  protected getCaption(): Snippet {
    return LangFranchiseeGuidesView.guides();
  }

  protected async internalSaveData(): Promise<boolean> {
    return true;
  }

  /** Every guide this section offers, in the order they are listed. */
  private guides(): GuideDefinition[] {
    return [
      {
        id: 'export-format',
        title: () => LangFranchiseeGuidesView.exportFormat(),
        render: () => this.exportFormatGuide()
      }
    ];
  }

  /** Falls back to the first guide, so an unknown id can never leave the page blank. */
  private get selectedGuide(): GuideDefinition {
    const guides = this.guides();
    return guides.find(x => x.id === this.selectedGuideId) ?? guides[0];
  }

  protected bodyTemplate(): EventTemplate {
    const selected = this.selectedGuide;

    return html`
      <div class="franchisee-guides-view">
        <nav class="guides-nav" aria-label="${LangFranchiseeGuidesView.guides()}">
          <webmodule-tree
            selection="leaf"
            @webmodule-selection-change=${(e: CustomEvent<{ selection: HTMLElement[] }>) => {
              const id = e.detail.selection[0]?.dataset.guideId;
              if (id) this.selectedGuideId = id;
            }}
          >
            ${map(
              this.guides(),
              guide => html`
                <webmodule-tree-item data-guide-id=${guide.id} ?selected=${guide.id === selected.id}>
                  ${guide.title()}
                </webmodule-tree-item>
              `
            )}
          </webmodule-tree>
        </nav>

        <article class="guides-content">
          <h2>${selected.title()}</h2>
          ${selected.render()}
        </article>
      </div>
    `;
  }

  /** The Export Format guide: plain language first, then the technical specification. */
  private exportFormatGuide(): TemplateResult {
    return html`
      <!--
        The plain language explanation comes first and is open by default. Someone who does not
        write software has to be able to read the top of this and understand what the file is for
        without meeting a field table.
      -->
      <div class="pb-3">${asMarkdownTemplate(LangFranchiseeGuidesView.plainWhatItIs())}</div>

      <webmodule-details summary="${LangFranchiseeGuidesView.plainCostWarningSummary()}" open>
        ${asMarkdownTemplate(LangFranchiseeGuidesView.plainCostWarning())}
      </webmodule-details>

      <h3 class="pt-4">${LangFranchiseeGuidesView.technicalHeading()}</h3>
      <div class="pb-3">
        ${asMarkdownTemplate(LangFranchiseeGuidesView.technicalIntro())}
        ${asMarkdownTemplate(LangFranchiseeGuidesView.schemaVersion({ version: exportSchemaVersion }))}
      </div>

      <webmodule-details summary="${LangFranchiseeGuidesView.conventionsSummary()}">
        ${asMarkdownTemplate(LangFranchiseeGuidesView.conventions())}
      </webmodule-details>

      <webmodule-details summary="${LangFranchiseeGuidesView.structureSummary()}">
        ${asMarkdownTemplate(LangFranchiseeGuidesView.structure())}
      </webmodule-details>

      <webmodule-details summary="${LangFranchiseeGuidesView.stabilitySummary()}">
        ${asMarkdownTemplate(LangFranchiseeGuidesView.stability())}
      </webmodule-details>

      <webmodule-details summary="${LangFranchiseeGuidesView.quoteFieldsSummary()}">
        ${this.fieldTable(quoteExportFields)}
      </webmodule-details>

      <webmodule-details summary="${LangFranchiseeGuidesView.orderFieldsSummary()}">
        ${this.fieldTable(purchaseOrderExportFields)}
      </webmodule-details>

      <webmodule-details summary="${LangFranchiseeGuidesView.samplesSummary()}">
        ${asMarkdownTemplate(LangFranchiseeGuidesView.samplesIntro())}
        ${this.sample(LangFranchiseeGuidesView.sampleQuoteJson(), quoteExportSampleJson)}
        ${this.sample(LangFranchiseeGuidesView.sampleQuoteXml(), quoteExportSampleXml)}
        ${this.sample(LangFranchiseeGuidesView.sampleOrderJson(), purchaseOrderExportSampleJson)}
        ${this.sample(LangFranchiseeGuidesView.sampleOrderXml(), purchaseOrderExportSampleXml)}
      </webmodule-details>
    `;
  }

  /**
   * The generated field reference, rendered with the application's own table component so it
   * behaves and themes like every other table in the product.
   *
   * Column widths are percentages (`cw-*`) rather than the fixed pixel `colpx-*` classes the data
   * grids use. The widest thing in the Meaning column is a sentence, so it should take whatever
   * width the window gives it and reflow down to tablet, where fixed pixels would either waste
   * space or overflow.
   */
  private fieldTable(fields: ExportFieldReferenceEntry[]): TemplateResult {
    const columns: WebModuleLitTableColumnDef[] = [
      {
        title: () => LangFranchiseeGuidesView.columnPath(),
        fieldName: 'path',
        classes: 'cw-30 alignment-left guides-field-path',
        displayValue: (_table: WebModuleLitTable, item: ExportFieldReferenceEntry) => html`<code>${item.path}</code>`
      },
      {
        title: () => LangFranchiseeGuidesView.columnType(),
        fieldName: 'type',
        classes: 'cw-18 alignment-left',
        displayValue: (_table: WebModuleLitTable, item: ExportFieldReferenceEntry) => html`${item.type}`
      },
      {
        title: () => LangFranchiseeGuidesView.columnPresence(),
        fieldName: 'presence',
        classes: 'cw-10 alignment-left',
        displayValue: (_table: WebModuleLitTable, item: ExportFieldReferenceEntry) => html`${item.presence}`
      },
      {
        title: () => LangFranchiseeGuidesView.columnMeaning(),
        fieldName: 'meaning',
        classes: 'cw-42 alignment-left guides-field-meaning',
        displayValue: (_table: WebModuleLitTable, item: ExportFieldReferenceEntry) => html`${item.meaning}`
      }
    ];

    return html`
      ${asMarkdownTemplate(LangFranchiseeGuidesView.fieldsIntro())}
      <webmodule-lit-table
        class="data-table guides-field-table"
        .keyevent=${(item: ExportFieldReferenceEntry) => item.path}
        .rowClass=${'row'}
        .colClass=${'col'}
        .data=${fields}
        .columns=${columns}
        pageLength=${10000}
        ?clickrows=${false}
      >
      </webmodule-lit-table>
    `;
  }

  /**
   * A worked example. Shown verbatim in a preformatted block, because whitespace and line breaks
   * are part of what the reader is checking their own file against.
   */
  private sample(caption: string, content: string): TemplateResult {
    return html`
      <webmodule-details summary="${caption}">
        <pre class="guides-sample"><code>${content}</code></pre>
      </webmodule-details>
    `;
  }
}
